import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Wind, Layers, Wrench, Paintbrush, LayoutPanelTop, Fence, Home, Leaf, TreePine, Trash2 } from "lucide-react";

const ICONS = {
  hvac: Wind,
  flooring: Layers,
  plumbing: Wrench,
  painting: Paintbrush,
  drywall: LayoutPanelTop,
  fences: Fence,
  roofing: Home,
  "landscaping-and-lawn-maintenance": Leaf,
  "tree-removal-and-land-clearing": TreePine,
  "trash-removal-and-move-out": Trash2,
};

const BLURBS = {
  hvac: "AC repair, replacement, and seasonal tune-ups",
  flooring: "LVP, hardwood, tile, and carpet installation",
  plumbing: "Leaks, water heaters, drains, and repipes",
  painting: "Interior, exterior, and cabinet painting",
  drywall: "Patches, texture matching, and new walls",
  fences: "Wood, vinyl, and aluminum fences and gates",
  roofing: "Inspections, repairs, and replacements",
  "landscaping-and-lawn-maintenance": "Mowing, cleanups, mulch, and irrigation",
  "tree-removal-and-land-clearing": "Tree work, stump grinding, and lot clearing",
  "trash-removal-and-move-out": "Junk hauling, cleanouts, and turnovers",
};

const NAMES = {
  hvac: "HVAC",
  flooring: "Flooring",
  plumbing: "Plumbing",
  painting: "Painting",
  drywall: "Drywall",
  fences: "Fences",
  roofing: "Roofing",
  "landscaping-and-lawn-maintenance": "Landscaping & Lawn",
  "tree-removal-and-land-clearing": "Tree Removal & Land Clearing",
  "trash-removal-and-move-out": "Trash Removal & Move-Out",
};

export const HomeServices = () => {
  return (
    <section id="home-services" data-testid="home-services-section" className="bg-[#EAF7FC] py-16 md:py-24 scroll-mt-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl mb-10 md:mb-14">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.5 }}
            className="text-xs uppercase tracking-[0.24em] font-bold text-[#54BCE6] mb-4"
          >
            Everyday home services
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.6 }}
            className="font-display text-[#133955] font-extrabold tracking-tighter text-4xl md:text-5xl lg:text-6xl leading-[0.98]"
          >
            The Right Specialist for the Job.
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.6, delay: 0.08 }}
            className="mt-5 text-base md:text-lg text-[#173042]/75 max-w-xl"
          >
            From a failing AC to a fence after a storm — tell us what you need and we'll help
            connect you with a specialist who covers your area.
          </motion.p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
          {Object.keys(ICONS).map((slug, i) => {
            const Icon = ICONS[slug];
            return (
              <motion.a
                key={slug}
                href={`/sc/home-services/${slug}/`}
                data-testid={`home-service-card-${slug}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: (i % 5) * 0.05 }}
                className="group rounded-2xl bg-white border border-[rgba(19,57,85,0.08)] p-5 hover:border-[#54BCE6] hover:shadow-[0_12px_32px_rgba(19,57,85,0.10)] transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#54BCE6]"
              >
                <span className="grid place-items-center w-10 h-10 rounded-full bg-[#EAF7FC] text-[#133955] group-hover:bg-[#54BCE6] transition-colors">
                  <Icon size={18} />
                </span>
                <h3 className="mt-4 font-display font-bold text-[#133955] leading-snug group-hover:text-[#54BCE6] transition-colors">{NAMES[slug]}</h3>
                <p className="mt-1.5 text-xs text-[#173042]/60 leading-relaxed">{BLURBS[slug]}</p>
                <span className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-[#54BCE6]">
                  Learn more <ArrowUpRight size={13} />
                </span>
              </motion.a>
            );
          })}
        </div>
      </div>
    </section>
  );
};
